/**
 * ManagerApprovalDialog
 *
 * Generic PIN-entry dialog used when a manager override is required
 * (high discounts, voiding sales, etc.).  It calls POST /auth/pos-pin-verify
 * and resolves/rejects based on the result.
 *
 * Usage:
 *   <ManagerApprovalDialog
 *     open={needsApproval}
 *     reason="Discount exceeds 20% — manager approval required"
 *     onApproved={(manager) => proceed()}
 *     onCancel={() => setNeedsApproval(false)}
 *   />
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { ShieldCheck } from 'lucide-react';
import api from '@/lib/api';

interface ManagerInfo {
  id: string;
  name: string;
  email: string;
}

interface Props {
  open: boolean;
  reason?: string;
  onApproved: (manager: ManagerInfo) => void;
  onCancel: () => void;
}

export function ManagerApprovalDialog({ open, reason, onApproved, onCancel }: Props) {
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');

  const mutation = useMutation({
    mutationFn: (pin: string) =>
      api.post('/auth/pos-pin-verify', { pin }).then((r) => r.data as ManagerInfo),
    onSuccess: (manager) => {
      setPinInput('');
      setPinError('');
      onApproved(manager);
    },
    onError: () => {
      setPinError('Incorrect PIN. Please try again.');
      setPinInput('');
    },
  });

  const handleKey = (key: string) => {
    if (mutation.isPending) return;
    if (key === '⌫') {
      setPinInput((p) => p.slice(0, -1));
      setPinError('');
    } else if (pinInput.length < 4) {
      const next = pinInput + key;
      setPinInput(next);
      setPinError('');
      if (next.length === 4) mutation.mutate(next);
    }
  };

  const handleClose = () => {
    setPinInput('');
    setPinError('');
    onCancel();
  };

  return (
    <Dialog
      visible={open}
      onHide={handleClose}
      header={
        <div className="flex items-center gap-2 text-warning-text">
          <ShieldCheck className="h-5 w-5" />
          <span>Manager Approval Required</span>
        </div>
      }
      modal
      style={{ width: '320px' }}
      breakpoints={{ '640px': '95vw' }}
    >
      <div className="space-y-4">
        {reason && (
          <p className="rounded-lg bg-warning/10 border border-warning/30 px-3 py-2 text-sm text-warning-text">
            {reason}
          </p>
        )}
        <p className="text-sm text-muted-foreground text-center">
          A manager must enter their PIN to authorise this action.
        </p>

        {/* PIN dots */}
        <div className="flex justify-center gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-4 w-4 rounded-full border-2 transition-all ${
                i < pinInput.length
                  ? 'bg-warning border-warning'
                  : 'bg-muted border-border'
              }`}
            />
          ))}
        </div>

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-2 mx-auto max-w-[240px]">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key) => (
            <button
              key={key || 'empty'}
              disabled={!key || mutation.isPending}
              onClick={() => key && handleKey(key)}
              className={`h-12 rounded-lg text-lg font-semibold border transition-colors ${
                !key
                  ? 'invisible'
                  : 'bg-muted hover:bg-muted/80 active:scale-95 disabled:opacity-60'
              }`}
            >
              {key}
            </button>
          ))}
        </div>

        {pinError && (
          <p className="text-center text-xs text-destructive-text font-medium">{pinError}</p>
        )}
        {mutation.isPending && (
          <p className="text-center text-xs text-muted-foreground">Verifying…</p>
        )}
      </div>
    </Dialog>
  );
}
